import {Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges} from '@angular/core';
import {Donation} from "../donation/donation";
import {TranslateService} from "@ngx-translate/core";
import {OptionService} from "../option/option.service";
import {DecimalPipe} from "@angular/common";

@Component({
    selector: 'step-index',
    templateUrl: './output.stepindex.component.html',
    providers: [DecimalPipe]
})
export class StepindexComponentComponent implements OnInit, OnChanges {
    /**
     * List of steps that are available
     */
    @Input() steps: any;

    /**
     * current active step
     */
    @Input() step: string;

    /**
     * Output parameter for changing the step
     * @type {EventEmitter}
     */
    @Output() stepChange: EventEmitter<any> = new EventEmitter();

    /**
     * One step from which on we can show the donation amount
     */
    @Input() donationAmountStartStep: string = null;

    /**
     * The current donation
     */
    @Input() donation: Donation;

    /**
     * Css class for the wrapper of the element. Can be overwritten by customWidget
     */
    @Input() wrapperClass: string = 'step-index';

    /**
     * List of all steps and their index (ordering)
     */
    private stepIndexes = {};

    /**
     * Should the element stay hidden
     */
    hide: boolean = false;

    /**
     * List of the main menu items
     */
    menu: any = [];

    /**
     * List of steps where clicking the visited steps is not allowed
     */
    private menuNotClickableSteps = ['thankyou', 'end'];

    /**
     * List of steps where clicking the later steps is allowed
     */
    private menuClickableLaterSteps = ['thankyou'];

    /**
     * List of steps that show a back-buttons
     */
    backbuttonSteps: any = {'banktransfer': 'thankyou', 'contact': 'thankyou'};

    /**
     * Holds the type of the project
     */
    projectType: string = null;

    /**
     * @constructor
     * @param translate
     * @param optionService
     * @param decimalPipe
     */
    constructor(private translate: TranslateService, private optionService: OptionService, private decimalPipe: DecimalPipe) {}

    /**
     * @inheritDoc
     */
    ngOnInit(): void {
        // extract the visible steps for the menu
        this.menu = Object.keys(this.steps);

        // init step order
        this.initStepIndexes();

        // if we have less or equal steps than 2 on fist page, hide the component
        if(this.menu.length <= 2) {
            this.hide = true;
        }

        // get the projecttype
        this.projectType = this.optionService.getProjectType();
    }

    /**
     * Check form some changes
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if(changes.steps &&
            changes.steps.isFirstChange() === false &&
            Object.keys(changes.steps.currentValue).length != Object.keys(changes.steps.previousValue).length) {
            this.steps = changes.steps.currentValue;
            this.ngOnInit();
        }

    }

    /**
     * extracts the order of the steps from the steplist
     */
    private initStepIndexes(): void {
        let index = 0;

        this.stepIndexes = {};
        for(let menuStep in this.steps) {
            this.stepIndexes[menuStep] = index;

            for(let submenuStep of this.steps[menuStep]) {
                this.stepIndexes[submenuStep] = index;
            }

            index++;
        }
    }

    /**
     * Returns the css class for a specific step
     * @param {string} step
     * @returns {string}
     */
    getClass(step: string): string {
        let currentIndex = this.stepIndexes[this.step];
        let stepIndex = this.stepIndexes[step];

        let classes = "step-" + step;

        if(currentIndex == stepIndex) {
            classes += " active";
        }
        else if (stepIndex < currentIndex) {
            classes += " visited";

            if(this.menuNotClickableSteps.indexOf(this.step) === -1) {
                classes += " clickable"
            }
        }
        return classes;
    }

    /**
     * returns the text for the menu buttons
     * @param {string} step
     * @returns {string}
     */
    getText(step: string): string {
        let extraText = "";

        switch (step) {
            case 'contact-mandatory':
                step = 'contact';
                break;
            case 'payment':
                if(this.step != 'start' &&
                    this.step != 'contact-mandatory' &&
                    this.donation.paymentMethod &&
                    this.donation.paymentMethod.length > 0) {
                    extraText = '<i class="twingle-' + this.donation.paymentMethod + '"></i>';
                }

        }

        // textkey for the name of the step
        return extraText + "<span>" + this.translate.instant('stepindex.step.' + step) + "</span>";
    }

    /**
     * On some steps in the menu we want to show some addtional text in the button
     * @param step
     */
    getAdditionalText(step: string): string {
        switch (step) {
            case 'start':
                if(this.step != 'start') {
                    let text: string = "";
                    if(this.projectType === 'membership') {
                        if (this.donation.rythm === 'one_time') {
                            text += this.translate.instant("rythm.one_time.label");
                        } else {
                            text += this.translate.instant("rythm.recurring." + this.donation.recurringRythm + ".label.membership", this.getPlaceholders());
                        }
                    }
                    else {
                        text += '<span>' + this.formatAmount(this.donation.amount) + ' ' + this.donation.currencySymbol + '</span> ';
                        if (this.donation.rythm === 'one_time') {
                            text += this.translate.instant("rythm.one_time.label");
                        } else {
                            text += this.translate.instant("rythm.recurring." + this.donation.recurringRythm + ".label", this.getPlaceholders());
                        }
                    }

                    return text;
                }
                break;
            case 'payment':
                if(this.step != 'start' &&
                    this.step != 'contact-mandatory' &&
                    this.donation.paymentMethod &&
                    this.donation.paymentMethod.length > 0) {
                    // remove line breaks and html from payment method
                    let div = document.createElement("div");
                    div.innerHTML = this.translate.instant("payment." + this.donation.paymentMethod + ".label").replace(/<br ?\/?>/, " ")?? "";
                    return div.textContent || div.innerText || "";
                }
                break;
        }

        return "";
    }

    /**
     * Function to navigate to another steo
     * @param {string} step
     */
    changeStep(step: string): void {

        let currentIndex = this.stepIndexes[this.step];
        let stepIndex = this.stepIndexes[step];

        // only change step if the requested step is visited
        if((stepIndex < currentIndex && this.menuNotClickableSteps.indexOf(this.step) === -1) ||
            (this.menuClickableLaterSteps.indexOf(this.step) !== -1 && stepIndex > currentIndex)) {
            this.step = step;
            this.stepChange.emit(this.step);
        }
    }

    /**
     * Change step without any chcks
     * @param step
     */
    forceChangeStep(step: string): void {
        this.step = step;
        this.stepChange.emit(this.step);
    }

    /**
     * Returns if we have to show a backbutton on the current step
     */
    showBackButton(): boolean {
        return typeof this.backbuttonSteps[this.step] != "undefined";
    }

    /**
     * Returns a list of placeholders that we need inside the last submit button in the widget
     */
    getPlaceholders(): any {
        return {
            donationAmount: this.formatAmount(this.donation.amount),
            donationAmountx3: this.formatAmount(this.donation.amount * 3),
            donationAmountx6: this.formatAmount(this.donation.amount * 6),
            donationAmountx12: this.formatAmount(this.donation.amount * 12),
            currencySymbol: this.donation.currencySymbol
        };
    }

    /**
     * Returns a formatted amount and the extra amount if set
     * @param amount
     */
    formatAmount(amount: number): string {
        // check if amount has decimals
        const hasDecimals = amount % 1 !== 0;

        let amountString = this.decimalPipe.transform(amount, hasDecimals ? '1.2-2' : '1.0-0');
        if(this.donation.extraAmount) {
            amountString += ' + ' + this.decimalPipe.transform(this.donation.extraAmount, '1.0-0');
        }
        return amountString;
    }
}
